<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'dunning-letters',
    path: '/dunning-letters',
    component: 'DunningLetters',
    title: () => pgettext('DunningLetters', 'Dunning Letters'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'DunningLetters',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'reference_number',
      name: $fn.value.pgettext('DunningLetters', 'Reference Number'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'dunningLetter',
        params: {
          id: 'id',
        },
      },
    },
    {
      field: 'status',
      name: $fn.value.pgettext('DunningLetters', 'Status'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'gettext',
            context: 'DunningLetterStatus',
            conversion: 'StartCase',
          },
          {
            name: 'badge',
            variantmap: {
              sent: 'light',
              delivered: 'success',
              error: 'danger',
            },
            variantMapSource: 'fieldValue',
          },
        ],
      },
    },
    {
      field: 'company_name',
      name: $fn.value.pgettext('DunningLetters', 'Owner company'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'company_id',
        },
      },
    },
    {
      field: 'customer_name',
      name: $fn.value.pgettext('DunningLetters', 'Partner'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'client_id',
        },
      },
    },
    {
      field: 'customer_email',
      name: $fn.value.pgettext('DunningLetters', 'Client Email'),
      formatter: {
        name: 'join',
        separator: '; ',
      },
    },
    {
      field: 'severity',
      name: $fn.value.pgettext('DunningLetters', 'Severity'),
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('DunningLetters', 'Created at'),
      formatter: {
        name: 'datetime',
      },
    },
    {
      field: 'created_by',
      name: $fn.value.pgettext('DunningLetters', 'Created by'),
      formatter: {
        name: 'user',
      },
    },
  ]);
  const b_2248226175642056_contextMenu = computed(() => ({
    items: [
      {
        label: 'Delete',
        command: 'remove',
      },
    ],
  }));

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
    b_2248226175642056_contextMenu: b_2248226175642056_contextMenu.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('integro_api/list_dunning_letter');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_contextMenu', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
      b_2248226175642056_contextMenu,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('DunningLetters', 'Dunning Letters'),
      icon: $config.value.dunningLetter.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_70rjjmen4_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_6kzsxrfxb_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_script_unm1m99yj_value_callable = async (
    $event
  ) => {
    return $refs.grid.reload();
  };
  const act_2248226175642056_script_unm1m99yj_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_unm1m99yj_value_callable($event);
      },
    };
  };
  const act_2248226175642056_request_lrpw5u454_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_dunning_letter',
      parameters: {
        id:
          ($event.data.data && $event.data.data.id) ||
          ($event.data.item && $event.data.item.id) ||
          $event.data.id,
      },
    };
  };
  const act_4615685607825208_showModal_jz7v96jsq_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'SendMails',
      props: {
        items: selectedItems.value,
      },
    };
  };
  const act_2095089031469157_script_vyf8f6idi_value_callable = async (
    $event
  ) => {
    return $refs.grid.reload();
  };
  const act_2095089031469157_script_vyf8f6idi_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2095089031469157_script_vyf8f6idi_value_callable($event);
      },
    };
  };
  const act_2095089031469157_request_tkghe61x5_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_delete_dunning_letter',
      data: selectedItems.value.map((p) => ({ id: p.id, etag: null })),
      parameters: { id: selectedItems.value.map((p) => p.id) },
    };
  };

  const act_7315092382398562_closeModal_70rjjmen4 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_70rjjmen4_getActionArgs,
    });
  const inst_evh_7315092382398562_close_hrdomnwbx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_70rjjmen4,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_hrdomnwbx],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_71iq6j3oj =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_10medzfhv =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_71iq6j3oj,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_10medzfhv],
    event: 'reload',
  });
  const act_2248226175642056_setData_6kzsxrfxb = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_6kzsxrfxb_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_j6uue58c7 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_6kzsxrfxb,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_j6uue58c7],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_script_unm1m99yj = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_unm1m99yj_getActionArgs,
  });
  const inst_evh_2248226175642056_success_f7vi65tpa =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_unm1m99yj,
      event: 'success',
      displayName: 'script',
    });
  const act_2248226175642056_request_lrpw5u454 = new actionTypes.RequestAction({
    actionArgs: act_2248226175642056_request_lrpw5u454_getActionArgs,
    events: [inst_evh_2248226175642056_success_f7vi65tpa],
  });
  const inst_evh_2248226175642056_contextmenuRemove_c8j7bue9i =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_request_lrpw5u454,
      event: 'contextmenu:remove',
      displayName: 'request',
    });
  const inst_evh_2248226175642056_contextmenuRemove =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_contextmenuRemove_c8j7bue9i],
      event: 'contextmenu:remove',
    });
  const act_4615685607825208_showModal_jz7v96jsq =
    new actionTypes.ShowModalAction({
      actionArgs: act_4615685607825208_showModal_jz7v96jsq_getActionArgs,
    });
  const inst_evh_4615685607825208_click_kunzwaavs =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_showModal_jz7v96jsq,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_4615685607825208_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4615685607825208_click_kunzwaavs],
    event: 'click',
  });
  const act_2095089031469157_script_vyf8f6idi = new actionTypes.ScriptAction({
    actionArgs: act_2095089031469157_script_vyf8f6idi_getActionArgs,
  });
  const inst_evh_2095089031469157_success_4vjj41c23 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_script_vyf8f6idi,
      event: 'success',
      displayName: 'script',
    });
  const act_2095089031469157_request_tkghe61x5 = new actionTypes.RequestAction({
    actionArgs: act_2095089031469157_request_tkghe61x5_getActionArgs,
    displayName: 'deleteItems',
    events: [inst_evh_2095089031469157_success_4vjj41c23],
  });
  const inst_evh_2095089031469157_click_eahn57kk0 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_request_tkghe61x5,
      event: 'click',
      displayName: 'deleteItems',
    });
  const inst_evh_2095089031469157_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_click_eahn57kk0],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_hrdomnwbx,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_10medzfhv,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_j6uue58c7,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_success_f7vi65tpa,
    inst_evh_2248226175642056_contextmenuRemove_c8j7bue9i,
    inst_evh_2248226175642056_contextmenuRemove,
    inst_evh_4615685607825208_click_kunzwaavs,
    inst_evh_4615685607825208_click,
    inst_evh_2095089031469157_success_4vjj41c23,
    inst_evh_2095089031469157_click_eahn57kk0,
    deleteItems: inst_evh_2095089031469157_click_eahn57kk0,
    inst_evh_2095089031469157_click,
  };

  const evh_7315092382398562_close_hrdomnwbx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_hrdomnwbx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_10medzfhv = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_10medzfhv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_j6uue58c7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_j6uue58c7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_success_f7vi65tpa = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_success_f7vi65tpa.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_contextmenuRemove_c8j7bue9i = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_contextmenuRemove_c8j7bue9i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_contextmenuRemove = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_contextmenuRemove.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click_kunzwaavs = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click_kunzwaavs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success_4vjj41c23 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success_4vjj41c23.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click_eahn57kk0 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click_eahn57kk0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="DunningLetters"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="dunning-letters"
      :headerTitle="pgettext('DunningLetters', 'Dunning letters')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('filename', 'dunning-letters')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      :contextMenu="b_2248226175642056_contextMenu"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @contextmenu:remove="evh_2248226175642056_contextmenuRemove($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <ItpButton
            icon="fa-envelope"
            :text="pgettext('DunningLetters', 'Send emails...')"
            size="sm"
            variant="primary"
            :disabled="!selectedItems || !selectedItems.length"
            @click="evh_4615685607825208_click($event, {})"
          >
          </ItpButton>
          <ItpButton
            icon="fa-trash"
            :text="pgettext('DunningLetters', 'Delete')"
            size="sm"
            variant="danger"
            :disabled="!selectedItems || !selectedItems.length"
            :spinning="actions.deleteItems.isRunning"
            @click="evh_2095089031469157_click($event, {})"
          >
          </ItpButton>
          <PageHelp path="/claims/dunning-letters.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
